import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {
  trackDataClassificationError,
  trackMissingDatagroupError,
  trackUnsupportedActionError,
} from 'common/report/queries/utils';
import {WarningTheme} from 'common/components/warning/constants';
import {assertUnreachable} from 'common/util/typescript';
import {panel2react} from 'common/util/react';

import {promptFeedbackModal} from '../feedback-modal';
import {FeedbackForFeature} from '../feedback-modal/types';
import css from './index.inline.styl';
import {template} from './template';
import {
  EXAMPLE_LINKS,
  WARNING_HERO_BASICS_LINKS,
  WarningHeroReportName,
  WarningHeroTheme,
  WarningTrackingError,
} from './constants';
import DASHBOARDS_EMPTY_IMAGE from './images/empty-dashboards.svg';
import INSIGHTS_EMPTY_IMAGE from './images/empty-insights.svg';
import FUNNELS_EMPTY_IMAGE from './images/empty-funnels.svg';
import LEXICON_EMPTY_IMAGE from './images/empty-lexicon.svg';
import FLOWS_EMPTY_IMAGE from './images/empty-flows.svg';
import EXPERIMENTS_EMPTY_IMAGE from './images/empty-experiments.svg';

export interface WarningHeroJsxProps {
  attrs?: {
    // mp-warning attributes
    'header-text': string;
    'body-text'?: string;
    'cta-href'?: string;
    'cta-text'?: string;
    //'img-src'?: string --> this one is set from the 'report' attribute

    // custom
    theme: WarningHeroTheme;
    'tracking-error'?: WarningTrackingError;
    report?: WarningHeroReportName;
    'margin-override'?: string;
    'is-staff-user'?: boolean;
  };
}

export class WarningHero extends Component<EmptyObj, WarningHeroJsxProps['attrs']> {
  static get attrsSchema(): AttrsSchema<WarningHeroJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      'body-text': `string`,
      'cta-href': `string`,
      'cta-text': `string`,
      'header-text': `string`,
      theme: {type: `string`, default: WarningHeroTheme.Default, enum: Object.values(WarningHeroTheme)},
      'tracking-error': `string`,
      report: `string`,
      'margin-override': `string`,
      'is-staff-user': `boolean`,
    };
  }

  get config() {
    return {
      template,
      useShadowDom: true,
      css,
      helpers: {
        handleCtaClick: () => this.dispatchEvent(new CustomEvent(`ctaClick`)),
        getImgSrc: () => {
          const theme = this.attr(`theme`);
          const reportName = this.attr(`report`);
          if (theme === WarningHeroTheme.EmptyReport && reportName) {
            switch (reportName) {
              case WarningHeroReportName.Dashboards:
                return DASHBOARDS_EMPTY_IMAGE;
              case WarningHeroReportName.Insights:
                return INSIGHTS_EMPTY_IMAGE;
              case WarningHeroReportName.Impact:
                return INSIGHTS_EMPTY_IMAGE;
              case WarningHeroReportName.Events:
              case WarningHeroReportName.EventsSidebar:
                // this case is only to make assertUnreachable happy but we don't use this code path
                return INSIGHTS_EMPTY_IMAGE;
              case WarningHeroReportName.Funnels:
                return FUNNELS_EMPTY_IMAGE;
              case WarningHeroReportName.Retention:
                return FUNNELS_EMPTY_IMAGE;
              case WarningHeroReportName.LookupTables:
                return LEXICON_EMPTY_IMAGE;
              case WarningHeroReportName.Flows:
                return FLOWS_EMPTY_IMAGE;
              case WarningHeroReportName.ExperimentReporting:
                return EXPERIMENTS_EMPTY_IMAGE;
              case WarningHeroReportName.PublicDashboards:
                return DASHBOARDS_EMPTY_IMAGE;
              case WarningHeroReportName.Signal:
                return EXPERIMENTS_EMPTY_IMAGE;
              case WarningHeroReportName.Users:
                return INSIGHTS_EMPTY_IMAGE;
              default:
                return assertUnreachable(reportName);
            }
          }
          return ``;
        },
        getTheme: (): WarningTheme => {
          const theme = this.attr(`theme`);
          if (Object.values(WarningTheme).includes(theme as WarningTheme)) {
            return theme as WarningTheme;
          }
          return WarningTheme.Default;
        },
        getBodyText: () => {
          if (this.attr(`body-text`)) {
            return this.attr(`body-text`);
          } else if (this.attr(`theme`) === WarningHeroTheme.EmptyReport) {
            const reportName = this.attr(`report`);
            if (reportName) {
              const basicLink = WARNING_HERO_BASICS_LINKS[reportName];
              const exampleLink = EXAMPLE_LINKS[reportName];
              switch (reportName) {
                case WarningHeroReportName.Dashboards:
                  return `Learn [Board Basics](${basicLink}) or explore [Example Boards](${exampleLink})`;
                case WarningHeroReportName.Insights:
                  return `Learn [Insights Basics](${basicLink}) or explore [Example Insights Reports](${exampleLink})`;
                case WarningHeroReportName.Funnels:
                  return `Learn [Funnels Basics](${basicLink}) or explore [Example Funnels Reports](${exampleLink})`;
                case WarningHeroReportName.Impact:
                  return `Learn more on [Intro to Impact](${basicLink})`;
                case WarningHeroReportName.Retention:
                  return `Learn [Retention Basics](${basicLink}) or explore [Example Retention Reports](${exampleLink})`;
                case WarningHeroReportName.Flows:
                  return `Learn [Flows Basics](${basicLink}) or explore [Example Flows Reports](${exampleLink})`;
                case WarningHeroReportName.ExperimentReporting:
                  return `Learn more on [Intro to Experiments](${basicLink})`;
                case WarningHeroReportName.LookupTables:
                  return `Learn more with [Introduction to Lookup Tables](${basicLink}) or watch a [quick video](${exampleLink})`;
                case WarningHeroReportName.PublicDashboards:
                  return `No reports have been added yet`;
                case WarningHeroReportName.Signal:
                  return `Learn more on [Intro to Signal](${basicLink})`;
                case WarningHeroReportName.Events:
                case WarningHeroReportName.EventsSidebar:
                  // this case is only to make assertUnreachable happy but we don't use this code path
                  // TODO: add a help doc for new liveview like others
                  return `Please try a different query`;
                case WarningHeroReportName.Users:
                  // TODO: review this copy
                  return `Failed to load users, please try again later`;
                default:
                  return assertUnreachable(reportName);
              }
            }
          }

          return ``;
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    const theme = this.attr(`theme`);
    const trackingError = this.attr(`tracking-error`);
    const reportName = this.attr(`report`);
    if (theme === WarningHeroTheme.Sensitive && reportName) {
      trackDataClassificationError(reportName);
    }
    if (
      theme === WarningHeroTheme.Error &&
      trackingError === WarningTrackingError.MissingMappedDatagroup &&
      reportName
    ) {
      trackMissingDatagroupError(reportName);
    }
    if (
      theme === WarningHeroTheme.Error &&
      trackingError === WarningTrackingError.UnsupportedActionError &&
      reportName
    ) {
      trackUnsupportedActionError(reportName, this.attr(`body-text`));
    }
  }

  handleFeedBackButtonClicked = () => {
    promptFeedbackModal({
      feature: FeedbackForFeature.BehaviorMetrics,
      'is-staff-user': this.attr(`is-staff-user`),
    });
  };
}

export const WarningHeroEl = defineAndMakeJsxFactory<WarningHeroJsxProps>(`mp-warning-hero`, WarningHero);
export const WarningHeroReact = panel2react<WarningHeroJsxProps>(`mp-warning-hero`, WarningHero);
